import { useEffect, useState } from 'react';
import ChargerModule from '../../components/chargerModule';
import ProductForm from './sections/FormProduct';
import { useHistory, useParams } from 'react-router-dom';
import urlParameter from '../../utils/urlParameter';
import {
  createNewProduct,
  editProduct,
  getCategories,
  getProductDetail,
  updateProductCountryStoreStatus
} from '../../services/cataloge';
import { IcountryPricingData, ICreateProductResponse, IProductDetailId, ISkuDuplicate } from '../../types/products';
import { ICategory } from '../../types/category';
import { AxiosError } from 'axios';
import { DuplicateSkuNoticeModal } from './components';
import { IPendingStoreStatusChange } from './sections/FormProduct';
import { displayErrorNotification, displaySuccessNotification } from '../../utils/displayNudosStandardNotifications';
import useOnlyDevelopers from '../../state/setOnlyDevelopers';
import './CreateProduct.scss';

interface IProductType {
  id: number;
  type: string;
}

interface iParams {
  categoryId: string;
}
interface ISelectTab {
  title: string;
  number: number;
  view: boolean;
}
const CreateProduct = () => {
  const { push } = useHistory();
  const productId = urlParameter('productId');
  const { categoryId }: iParams = useParams();

  const [productDataDetail, setProductDataDetail] = useState<IProductDetailId>();
  const [loadingProduct, setLoadingProduct] = useState<boolean>(false);
  const [productType, setProductType] = useState<IProductType>({ id: 0, type: '' });
  const [selectTab, setSelectTab] = useState<ISelectTab>({ title: 'General', number: 1, view: true });
  const [skuDuplicate, setSkuDuplicate] = useState<ISkuDuplicate>();
  const [temporaryDataToForceCreation, setTemporaryDataToForceCreation] = useState<IProductDetailId>();
  const [loadingForceCreation, setLoadingForceCreation] = useState<boolean>(false);
  const { isOnlyDevsAutomates } = useOnlyDevelopers();

  /**
   * Aplica el estado en tienda (Prendido / Para cotizar / Apagado) de los países recién creados
   * vía el endpoint dedicado PATCH /store-status, leyendo el estado del payload del producto.
   * Solo aplica en modo developers; en otros casos se conserva el flujo actual (país activo vía POST).
   */
  const applyCreatedCountriesStoreStatus = async (productId?: number, pricingData?: IcountryPricingData[]) => {
    if (!isOnlyDevsAutomates || !productId || !pricingData?.length) return;
    for (const country of pricingData) {
      if (!country?.nameCode) continue;
      try {
        await updateProductCountryStoreStatus(productId, {
          countryCode: country.nameCode,
          isActive: !!country.isActive,
          isQuoteProduct: !!country.isQuoteProduct
        });
      } catch {
        // El refresh del detalle mostrará el estado real; no bloqueamos la creación por esto.
      }
    }
  };

  const headerInfo = {
    title: 'Catalogo',
    titleRoute: '/catalog/product-list',
    subtitle: productId ? 'Detalle' : 'Nuevo producto'
  };

  const infoSideBar = { position: 'catalog' };

  const asignCategory = async () => {
    try {
      const categoriesList = await getCategories();
      const category = parseInt(categoryId);
      const foundCategory = categoriesList.find((item: ICategory) => item.id === category);
      if (foundCategory && foundCategory?.id && foundCategory?.name) {
        setProductType({ id: foundCategory.id, type: foundCategory.name });
      } else {
        push('/catalog/category-type');
      }
    } catch (error) {
      displayErrorNotification();
    }
  };

  const getDataProductDetail = async (productId: string | number) => {
    setLoadingProduct(true);
    try {
      const dataProductDetail = await getProductDetail(productId);
      setProductDataDetail(dataProductDetail);
      setLoadingProduct(false);
    } catch (err) {
      setLoadingProduct(false);
      displayErrorNotification();
    }
  };

  const changeTab = (dataTab: ISelectTab) => {
    setSelectTab(dataTab);
  };

  /**
   * Persiste cambios del producto:
   * 1. PUT con la data general del producto (sin isActive/isQuoteProduct por país).
   * 2. PATCH /store-status por cada país recién agregado en sesión para fijar su estado en tienda.
   * 3. Refresca el detalle desde backend. Si algún PATCH falla, notifica al usuario indicando los países afectados.
   * @param dataForm Payload del producto para el PUT.
   * @param pendingStoreStatusChanges Lista de cambios de estado en tienda pendientes (países agregados en sesión).
   */
  const updateProduct = async (
    dataForm: IProductDetailId,
    pendingStoreStatusChanges: IPendingStoreStatusChange[] = []
  ) => {
    setLoadingProduct(true);
    const finalProfileIds = dataForm.profileIds ?? productDataDetail?.profileIds ?? [];

    const profilesMap = new Map((productDataDetail?.profiles || []).map(profile => [profile.id, profile]));

    const profiles = (finalProfileIds || []).map(profileId => {
      const existingProfile = profilesMap.get(profileId);
      return existingProfile || { id: profileId, name: '' };
    });

    const { profiles: _, ...dataFormWithoutProfiles } = dataForm;

    const formatData = {
      ...productDataDetail,
      ...dataFormWithoutProfiles,
      profileIds: finalProfileIds,
      profiles: profiles
    };
    try {
      await editProduct(productDataDetail?.productId || 0, formatData);

      const failedCountries: string[] = [];
      for (const change of pendingStoreStatusChanges) {
        try {
          await updateProductCountryStoreStatus(productDataDetail?.productId || 0, change);
        } catch {
          failedCountries.push(change.countryCode);
        }
      }

      await getDataProductDetail(productDataDetail?.productId || 0);
      setLoadingProduct(false);
      if (failedCountries.length > 0) {
        displayErrorNotification({
          customJSXMessage: (
            <>
              No se pudo aplicar el estado en tienda para: {failedCountries.join(', ')}. Puedes intentar nuevamente desde
              el selector del país.
            </>
          )
        });
      } else {
        displaySuccessNotification({ customJSXMessage: <>El producto fue editado correctamente</> });
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err?.response?.status === 400 && err?.response?.data?.error) {
        displayErrorNotification({ customJSXMessage: <>{err?.response?.data?.error}</> });
      } else {
        displayErrorNotification();
      }
      setLoadingProduct(false);
    }
  };

  const getDataOfTheCreatedProduct = async (productId?: number) => {
    if (!productId) push('/catalog/product-list');
    push(`/catalog/create-product/${categoryId}?productId=${productId}&sendToCatalog=true`);
    await getDataProductDetail(Number(productId));
    await asignCategory();
  };
  const forceProductCreation = async () => {
    if (!temporaryDataToForceCreation) return;
    setLoadingForceCreation(true);
    const forceData = { ...temporaryDataToForceCreation, forceCreationOfRepeatedSku: true };
    try {
      const productData = await createNewProduct(forceData);
      await applyCreatedCountriesStoreStatus(Number(productData?.data?.productId), forceData.pricingData);
      await getDataOfTheCreatedProduct(Number(productData?.data?.productId));
      displaySuccessNotification({ customJSXMessage: <>El producto se creo correctamente</> });
      setTemporaryDataToForceCreation(undefined);
      setSkuDuplicate(undefined);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingForceCreation(false);
    }
  };

  const createProduct = async (dataForm: IProductDetailId) => {
    setLoadingProduct(true);
    const addAttributes: IProductDetailId = {
      ...dataForm,
      categoryId,
      forceCreationOfRepeatedSku: false
    };
    setTemporaryDataToForceCreation({ ...addAttributes });
    try {
      const productData = await createNewProduct(addAttributes);
      await applyCreatedCountriesStoreStatus(Number(productData?.data?.productId), addAttributes.pricingData);
      displaySuccessNotification({ customJSXMessage: <>El producto se creo correctamente</> });
      const response: ICreateProductResponse = {
        status: 'success',
        message: 'creado correctamente',
        statusCode: 200,
        productId: Number(productData?.data?.productId)
      };
      setTemporaryDataToForceCreation(undefined);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      const duplicateSkuError =
        err?.response?.status === 409 &&
        err?.response?.data?.message &&
        JSON.parse(err?.response?.data?.message)?.message === 'Product duplicate';
      if (duplicateSkuError) {
        const formatMessage: ISkuDuplicate = JSON.parse(err?.response?.data?.message);
        setSkuDuplicate(formatMessage);
      }
      if (err?.response?.status === 400 && err?.response?.data?.error) {
        displayErrorNotification({ customJSXMessage: <>{err?.response?.data?.error}</> });
      } else if (err?.response?.status !== 409) {
        displayErrorNotification();
      }
      const errorResponse: ICreateProductResponse = {
        status: 'error',
        message: err?.response?.data?.message || 'no se creo el producto',
        statusCode: Number(err?.response?.status)
      };
      return errorResponse;
    } finally {
      setLoadingProduct(false);
    }
  };

  useEffect(() => {
    asignCategory();
    if (productId) {
      getDataProductDetail(productId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChargerModule headerInfo={headerInfo} infoSideBar={infoSideBar}>
      <div id="newProduct">
        {!!skuDuplicate && (
          <DuplicateSkuNoticeModal
            skuDuplicate={skuDuplicate}
            forceProductCreation={forceProductCreation}
            closeModal={() => setSkuDuplicate(undefined)}
            loadingForceCreation={loadingForceCreation}
            categoryId={categoryId}
          />
        )}
        {productType.id !== 0 && (
          <ProductForm
            typeProduct={productType}
            productDataDetail={productDataDetail}
            loadingProduct={loadingProduct}
            updateProduct={updateProduct}
            createProduct={createProduct}
            changeTab={changeTab}
            selectTab={selectTab}
            setProductDataDetail={setProductDataDetail}
            getDataOfTheCreatedProduct={getDataOfTheCreatedProduct}
          />
        )}
      </div>
    </ChargerModule>
  );
};
export default CreateProduct;
